import { h, Component } from 'preact';
import { CloverIcon, HelpButton, JoinButton, RoyalWinModal, RoyalWinOverlay, Winner, WinnersList, PromoHeader, PromoFooter } from '../mobile-templates/royal-wins';
import { SlotTournamentsService, LeaderboardEntry, PrizeTier, SuccessDetailsApiResponse } from '../../../services/slot-tournaments';
import { route } from 'preact-router';
import PageDataFeed from '../../../services/page-data-feed';

const STORAGE_KEY = 'royalWinLastSeenDate';

// Helper to get date in YYYY-MM-DD format
const getISODate = (date: Date): string => date.toISOString().split('T')[0];

class RoyalWinsShowCondition {
    private storage: Storage;

    constructor(storage: Storage) {
        this.storage = storage;
    }

    shouldShow(): boolean {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = getISODate(yesterday);
        const lastSeenDate = this.storage.getItem(STORAGE_KEY);
        return !lastSeenDate || lastSeenDate !== yesterdayStr;
    }

    markAsShown() {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        this.storage.setItem(STORAGE_KEY, getISODate(yesterday));
    }
}

class RoyalWinPlayersLoader {
    constructor(
        private readonly service: SlotTournamentsService
    ) {
    }

    async load(): Promise<{id: number, winners: Winner[]} | null> {
        try {
            const now = new Date();
            const tournamentList = await this.service.listTournaments('finished', 1, 100);
            if (!tournamentList.data || tournamentList.data.length === 0) {
                console.log("No tournament found.");
                return null;
            }

            // Filtra i tornei con end_date <= now e ordina per end_date decrescente
            const finishedTournaments = tournamentList.data
                .filter(t => new Date(t.end_date) <= now)
                .filter(t => t.title.startsWith("Victory Vault"))
                .filter(t => !t.title.includes("BONUS"))
                .sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime());

            if (finishedTournaments.length === 0) {
                console.log("No ended tournament found.");
                return null;
            }

            const latestTournament = finishedTournaments[0];

            const detailsResponse = await this.service.getTournamentDetails(latestTournament.id);

            if (detailsResponse.status === 'success') {
                return {id: latestTournament.id, winners: this.formatWinners(detailsResponse.data)};
            } else {
                console.error("Failed to fetch tournament details.");
                return null;
            }
        } catch (error) {
            console.error("Error fetching Royal Wins data:", error);
            return null;
        }
    }

    private formatWinners(data: SuccessDetailsApiResponse['data']): Winner[] {
        const { tournament, leaderboard } = data;
        const prizeMap = new Map<number, number>();
        tournament.prize_structure.forEach((tier: PrizeTier) => {
            for (let rank = tier.rank; rank <= (tier.rank_to || tier.rank); rank++)
                prizeMap.set(rank, tier.amount);
        });

        return leaderboard.map((entry: LeaderboardEntry) => ({
            censoredUsername: entry.username,
            amount: prizeMap.get(entry.rank) || 0,
        })).filter(winner => winner.amount > 0);
    }
}

interface RoyalWinsState {
    showModal: boolean;
    winners: Winner[];
    tournamentId?: number;
    isLoading: boolean;
}

export class RoyalWinsController extends Component<{}, RoyalWinsState> {
    private showCondition: RoyalWinsShowCondition = new RoyalWinsShowCondition(window.safeLocalStorage);
    private playersLoader: RoyalWinPlayersLoader = new RoyalWinPlayersLoader(new SlotTournamentsService());

    state: RoyalWinsState = {
        showModal: false,
        winners: [],
        isLoading: true,
    };


    componentDidMount() {
        if (this.showCondition.shouldShow()) {
            this.fetchAndShowWinners();
        } else {
            this.setState({ isLoading: false });
        }
    }

    fetchAndShowWinners = async () => {
        const tournament = await this.playersLoader.load();
        if (tournament) {
            this.setState({ winners: tournament.winners, tournamentId: tournament.id, showModal: true, isLoading: false });
        } else {
            this.setState({ isLoading: false });
        }
    };

    handleClose = () => {
        this.showCondition.markAsShown();
        this.setState({ showModal: false });
    };

    render() {
        const { isLoading, showModal, winners } = this.state;

        if (isLoading || !showModal) {
            return null;
        }

        return <RoyalWinOverlay onClose={this.handleClose}>
            <CloverIcon class="absolute top-10 right-10 w-16 h-16 text-blue-whale-600 opacity-20" />
            <PromoHeader onClose={this.handleClose}>
                <div class="text-white">
                    <div class="text-3xl font-bold text-altbrand-400">5,000,000 ብር</div>
                    <div class="text-xs">1000 PLAYERS x 3,000 MAX x 100 DAYS!</div>
                    <div class="text-xxs">ለመቶ ቀናቶች የሚቆይ 5,000,000 ብር ሽልማት፣ በየዕለቱ 50,000 ብር ለ1,000 ደንበኞች እስከ 3,000 ብር ድረስ፦ አሁኑኑ ይቀላቀሉ!</div>
                </div>
            </PromoHeader>
            <WinnersList winners={winners} />
            <PromoFooter>
                <HelpButton onClick={() => {this.handleClose();route("/promo/victory-vault")}} />
                <JoinButton onClick={() => {this.handleClose();route("/slot-tournaments/"+this.state.tournamentId)}} />
            </PromoFooter>
        </RoyalWinOverlay>
    }
}

export default RoyalWinsController;